import { tileKey, type WorldState } from "@dawn/engine";
import type {
  ChroniclePort,
  CompletedRunRecord,
  LoggerPort,
  StoragePort,
} from "./ports.ts";
import { defaultBrowserStorage } from "./ports.ts";

export const WIN_CHRONICLE_STORAGE_KEY = "dawn.winChronicle";
export const WIN_CHRONICLE_LIST_LIMIT = 5;
/** Matches production `CHRONICLE_SCHEMA_VERSION` / e2e seed payload. */
export const CHRONICLE_SCHEMA_VERSION = 1 as const;

const noopLogger: LoggerPort = {
  debug() {},
  info() {},
  warn() {},
};

type WinChroniclePayload = {
  schemaVersion: typeof CHRONICLE_SCHEMA_VERSION;
  records: CompletedRunRecord[];
};

function writeWinChroniclePayload(
  storage: StoragePort,
  records: readonly CompletedRunRecord[],
): boolean {
  const payload: WinChroniclePayload = {
    schemaVersion: CHRONICLE_SCHEMA_VERSION,
    records: [...records].slice(0, WIN_CHRONICLE_LIST_LIMIT),
  };
  try {
    storage.setItem(WIN_CHRONICLE_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

/** Storage-backed chronicle sink (localStorage or memory). */
export function createChroniclePort(storage: StoragePort): ChroniclePort {
  return {
    appendWinRecord(record) {
      const existing = readWinRecordsFromStorage(storage);
      return writeWinChroniclePayload(storage, [record, ...existing]);
    },
  };
}

function isCompletedRunRecord(value: unknown): value is CompletedRunRecord {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    typeof record.completedAt === "string" &&
    typeof record.clanName === "string" &&
    typeof record.turns === "number" &&
    typeof record.finalPopulation === "number" &&
    typeof record.finalFood === "number" &&
    typeof record.startTerrain === "string" &&
    typeof record.endTerrain === "string" &&
    typeof record.tilesExplored === "number" &&
    typeof record.mapTileCount === "number"
  );
}

export function buildCompletedRunRecordFromWorld(
  state: WorldState,
): CompletedRunRecord | null {
  const tiles = Object.values(state.tiles);
  const startTile = tiles.find((tile) => tile.isStartingTile);
  const endTile =
    state.tiles[tileKey(state.clan.currentTile.q, state.clan.currentTile.r)];
  const mapTileCount = tiles.length;

  if (!startTile || !endTile || mapTileCount <= 0) {
    return null;
  }

  const tilesExplored = tiles.filter(
    (tile) => tile.visibility === "revealed",
  ).length;

  return {
    completedAt: new Date().toISOString(),
    clanName: state.clan.clanName,
    turns: state.turn,
    finalPopulation: state.clan.population,
    finalFood: state.clan.foodSupplies,
    startTerrain: startTile.terrain,
    endTerrain: endTile.terrain,
    tilesExplored,
    mapTileCount,
  };
}

/** Appends a win chronicle entry from current world state. Returns the record when stored. */
export function recordVictoryChronicle(
  state: WorldState,
  chronicle: ChroniclePort,
  logger: LoggerPort = noopLogger,
): CompletedRunRecord | null {
  const record = buildCompletedRunRecordFromWorld(state);
  if (!record) {
    return null;
  }

  if (!chronicle.appendWinRecord(record)) {
    logger.warn("save", "win_chronicle_append_failed");
    return null;
  }

  return record;
}

export interface CareerAggregates {
  totalWins: number;
  fastestWinTurns: number | null;
  mostExploredFraction: number | null;
}

function sortNewestFirst(records: CompletedRunRecord[]): CompletedRunRecord[] {
  return [...records].sort(
    (left, right) =>
      Date.parse(right.completedAt) - Date.parse(left.completedAt),
  );
}

function readWinRecordsFromStorage(
  storage: StoragePort | null | undefined,
): CompletedRunRecord[] {
  if (!storage) {
    return [];
  }

  try {
    const raw = storage.getItem(WIN_CHRONICLE_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return sortNewestFirst(parsed.filter(isCompletedRunRecord));
    }

    if (parsed && typeof parsed === "object") {
      const payload = parsed as Record<string, unknown>;
      if (Array.isArray(payload.records)) {
        return sortNewestFirst(
          payload.records.filter(isCompletedRunRecord),
        );
      }
    }
  } catch {
    return [];
  }

  return [];
}

export function getWinRecords(
  storage: StoragePort | null = defaultBrowserStorage(),
): CompletedRunRecord[] {
  return readWinRecordsFromStorage(storage);
}

export function getRecentWinRecords(
  limit = WIN_CHRONICLE_LIST_LIMIT,
  storage: StoragePort | null = defaultBrowserStorage(),
): CompletedRunRecord[] {
  return getWinRecords(storage).slice(0, limit);
}

export function getCareerAggregates(
  storage: StoragePort | null = defaultBrowserStorage(),
): CareerAggregates {
  const records = readWinRecordsFromStorage(storage);

  if (records.length === 0) {
    return {
      totalWins: 0,
      fastestWinTurns: null,
      mostExploredFraction: null,
    };
  }

  let fastestWinTurns = records[0]!.turns;
  let mostExploredFraction = 0;

  for (const record of records) {
    if (record.turns < fastestWinTurns) {
      fastestWinTurns = record.turns;
    }

    if (record.mapTileCount > 0) {
      const fraction = record.tilesExplored / record.mapTileCount;
      if (fraction > mostExploredFraction) {
        mostExploredFraction = fraction;
      }
    }
  }

  return {
    totalWins: records.length,
    fastestWinTurns,
    mostExploredFraction: mostExploredFraction > 0 ? mostExploredFraction : null,
  };
}

export function clearWinChronicle(
  storage: StoragePort | null = defaultBrowserStorage(),
): void {
  storage?.removeItem(WIN_CHRONICLE_STORAGE_KEY);
}
